import React from "react";
import { useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useParams } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Loader from "../../../../components/loader/loader";
import Card from "../../../../components/card";
import "./rideview.css";
import {
  formatCurrency,
  getRideDetailsApi,
  getRideRatingsApi,
  getRideTransactionsApi,
  refundRidePaymentApi,
} from "services/customAPI";
import { FaArrowLeft, FaPhoneAlt } from "react-icons/fa";
import {
  MdOutlineAttachMoney,
  MdOutlineCreditCard,
  MdOutlineDirectionsCar,
  MdOutlineLocationOn,
  MdOutlinePerson,
  MdOutlineRoute,
  MdOutlineStar,
  MdOutlineTimeline,
  MdOutlineChat,
  MdOutlineMoneyOff,
} from "react-icons/md";
import { Link } from "react-router-dom";
import { t } from "i18next";
import { DTO, Types, Formatters } from "shared-lib";
import { getStatusText } from "utils/commons";
import { ImageWithFallbackProps } from "types/api.types";
import dummyCar from "../../../../assets/svg/dummyCar.svg";
import dummyProfile from "../../../../assets/svg/dummyProfile.svg";

// Fix default marker icons for Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function createLabelIcon(label: string, color: string): L.DivIcon {
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="
      background: ${color};
      color: white;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 12px;
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    ">${label}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

const center = { lat: -23.5489, lng: -46.6388 };

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  alt,
  fallbackSrc,
}) => {
  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = fallbackSrc;
  };
  return (
    <img
      src={src}
      alt={alt}
      onError={handleImageError}
      className="h-14 w-14 rounded-xl object-cover"
    />
  );
};

const StarDisplay = ({ stars }: { stars: number }) => (
  <span className="inline-flex gap-0.5 text-base">
    {[1, 2, 3, 4, 5].map((s) => (
      <span key={s} style={{ color: s <= stars ? "#FFD700" : "#3A3A4A" }}>
        ★
      </span>
    ))}
  </span>
);

type RideRating = {
  id?: number;
  rideId?: number;
  raterType?: "driver" | "rider";
  ratedType?: "driver" | "rider";
  stars?: number;
  comment?: string;
  createdAt?: string;
  raterDriver?: { firstName?: string; lastName?: string };
  raterRider?: { firstName?: string; lastName?: string };
  ratedDriver?: { firstName?: string; lastName?: string };
  ratedRider?: { firstName?: string; lastName?: string };
};

const getStatusColor = (status?: string) => {
  switch (status) {
    case Types.RideStatusEnum.COMPLETED:
      return { bg: "bg-green-500/15", text: "text-green-400", border: "border-green-500/30" };
    case Types.RideStatusEnum.CANCELLED:
      return { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" };
    default:
      return { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30" };
  }
};

const RideView = () => {
  const params = useParams();
  const [ride, setRide] = useState<DTO.RideDTO | null>(null);
  const [driverLocation, setDriverLocation] = useState<number[]>([]);
  const [rideTransactions, setRideTransactions] = useState<DTO.TransactionDTO[]>([]);
  const [rideRatings, setRideRatings] = useState<RideRating[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dateTimeCreatedAt, setDateTimeCreatedAt] = useState<Date | null>(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);

  const baseImageUrl =
    process.env.REACT_APP_BASE_IMAGE_URL ??
    `${process.env.REACT_APP_BASE_URL ?? ""}/uploads/`;
  const driverImagePath = ride?.driver?.profileImageKey
    ? `${baseImageUrl}${ride.driver.profileImageKey}`
    : "";
  const vehicleImagePath = ride?.driver?.vehicle?.profileImageKey
    ? `${baseImageUrl}${ride.driver.vehicle.profileImageKey}`
    : "";
  const riderImagePath = ride?.rider?.profileImageKey
    ? `${baseImageUrl}${ride.rider.profileImageKey}`
    : "";

  const convertPathToLatLng = (coords: number[][] | undefined): L.LatLngExpression[] => {
    if (!coords) return [];
    return coords.map((ele: number[]) => [ele[0] || 0, ele[1] || 0] as L.LatLngExpression);
  };

  const getData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getRideDetailsApi(Number(params.id));
      if (!res.data) {
        setIsLoading(false);
        return;
      }
      const rideData = res.data;
      setRide(rideData);
      setDateTimeCreatedAt(
        rideData.createdAt ? new Date(rideData.createdAt) : null
      );
      if (rideData.driver?.liveLocation) {
        setDriverLocation(rideData.driver.liveLocation);
      }
      const [transactionsResponse, ratingsResponse] = await Promise.all([
        getRideTransactionsApi(Number(params.id)),
        getRideRatingsApi(Number(params.id)),
      ]);
      const transactionsPayload = transactionsResponse?.data ?? transactionsResponse;
      const ratingsPayload = ratingsResponse?.data ?? ratingsResponse;
      setRideTransactions(transactionsPayload?.data ?? transactionsPayload ?? []);
      setRideRatings(ratingsPayload?.data ?? ratingsPayload ?? []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [params.id]);

  useEffect(() => {
    if (params.id) getData();
  }, [getData, params.id]);

  const mapCenter: L.LatLngExpression = ride?.pickUpLocation?.[0] && ride?.pickUpLocation?.[1]
    ? [ride.pickUpLocation[0], ride.pickUpLocation[1]]
    : [center.lat, center.lng];

  const statusInfo = getStatusColor(ride?.status);

  return (
    <>
      <Navbar flag={false} brandText={t("Ride details")} />
      <Link
        to="/admin/rides"
        className="mt-3 mb-1 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
      >
        <FaArrowLeft className="h-3 w-3" />
        {t("Voltar para Corridas")}
      </Link>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="mt-2 grid grid-cols-12 gap-4">
          {/* Left Column: Details */}
          <div className="col-span-12 lg:col-span-5 space-y-4">
            {/* Header Card */}
            <Card className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xl font-bold text-white">
                    {t("Corrida")} #{params.id}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    <span>📅 {dateTimeCreatedAt ? Formatters.formatDate(dateTimeCreatedAt) : "—"}</span>
                    <span>🕐 {dateTimeCreatedAt ? Formatters.formatTime(dateTimeCreatedAt) : "—"}</span>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.bg} ${statusInfo.text} border ${statusInfo.border}`}>
                  {getStatusText(undefined, ride?.status)}
                </span>
              </div>

              {/* Addresses */}
              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-500/15">
                    <MdOutlineLocationOn className="h-4 w-4 text-green-400" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">{t("Origem")}</div>
                    <div className="text-sm text-white">{ride?.pickUpAddress ?? "—"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-500/15">
                    <MdOutlineLocationOn className="h-4 w-4 text-red-400" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">{t("Destino")}</div>
                    <div className="text-sm text-white">{ride?.dropAddress ?? "—"}</div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3 bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <MdOutlineAttachMoney className="h-4 w-4 text-cyan-400" />
                    <span className="text-xs text-gray-400">{t("Tarifa")}</span>
                  </div>
                  <div className="text-lg font-bold text-white">{formatCurrency(ride?.fare)}</div>
                </div>
                <div className="rounded-xl p-3 bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <MdOutlineCreditCard className="h-4 w-4 text-purple-400" />
                    <span className="text-xs text-gray-400">{t("Pagamento")}</span>
                  </div>
                  <div className="text-sm font-semibold text-white uppercase">{ride?.paymentMode || "—"}</div>
                </div>
                <div className="rounded-xl p-3 bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <MdOutlineRoute className="h-4 w-4 text-green-400" />
                    <span className="text-xs text-gray-400">{t("Rota")}</span>
                  </div>
                  <div className="text-sm text-white">
                    {ride?.distance?.text || "—"} • {ride?.duration?.text || "—"}
                  </div>
                </div>
              </div>
            </Card>

            {/* Transactions */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <MdOutlineTimeline className="h-5 w-5 text-cyan-400" />
                <span className="text-lg font-semibold text-white">{t("Histórico de Pagamentos")}</span>
              </div>
              {rideTransactions.length === 0 ? (
                <div className="text-sm text-gray-500 bg-white/5 rounded-xl p-4 text-center">
                  {t("Nenhuma transação registrada para esta corrida")}
                </div>
              ) : (
                <div className="space-y-2">
                  {rideTransactions.map((tx, idx) => (
                    <div key={tx.id ?? idx} className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-4 py-3">
                      <div>
                        <div className="text-sm font-semibold text-white uppercase">{tx.operation}</div>
                        <div className="text-xs text-gray-400 uppercase">{tx.type}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-white">{formatCurrency(tx.amount)}</div>
                        <div className="text-xs text-gray-500">
                          {tx.createdAt ? Formatters.formatDateTime(tx.createdAt) : '—'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Ratings */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <MdOutlineStar className="h-5 w-5 text-amber-400" />
                <span className="text-lg font-semibold text-white">{t("Avaliações")}</span>
              </div>
              {rideRatings.length === 0 ? (
                <div className="text-sm text-gray-500 bg-white/5 rounded-xl p-4 text-center">
                  {t("Nenhuma avaliação para esta corrida")}
                </div>
              ) : (
                <div className="space-y-3">
                  {rideRatings.map((r, idx) => (
                    <div key={idx} className="rounded-xl bg-white/5 border border-white/10 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${r.raterType === 'driver' ? 'bg-blue-500/15 text-blue-400' : 'bg-purple-500/15 text-purple-400'
                            }`}>
                            {r.raterType === 'driver'
                              ? <MdOutlineDirectionsCar className="h-4 w-4" />
                              : <MdOutlinePerson className="h-4 w-4" />
                            }
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-white">
                              {r.raterType === 'driver' ? t('Motorista') : t('Passageiro')}
                            </div>
                            <div className="text-xs text-gray-400">
                              {r.raterType === 'driver'
                                ? `${r.raterDriver?.firstName || ''} ${r.raterDriver?.lastName || ''}`.trim()
                                : `${r.raterRider?.firstName || ''} ${r.raterRider?.lastName || ''}`.trim()
                              }
                              {' → '}
                              {r.ratedType === 'driver'
                                ? `${r.ratedDriver?.firstName || ''} ${r.ratedDriver?.lastName || ''}`.trim()
                                : `${r.ratedRider?.firstName || ''} ${r.ratedRider?.lastName || ''}`.trim()
                              }
                            </div>
                          </div>
                        </div>
                        <StarDisplay stars={r.stars || 0} />
                      </div>
                      {r.comment && (
                        <div className="text-sm text-gray-300 mt-2 pl-10 italic">"{r.comment}"</div>
                      )}
                      <div className="text-xs text-gray-500 mt-1 pl-10">
                        {r.createdAt ? Formatters.formatDateTime(new Date(r.createdAt)) : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Chat Messages — Modal Trigger */}
            {ride?.chatMessages && ride.chatMessages.length > 0 && (
              <Card className="p-5">
                <button
                  onClick={() => setChatModalOpen(true)}
                  className="flex items-center gap-3 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 hover:bg-white/10 transition-colors"
                >
                  <MdOutlineChat className="h-5 w-5 text-green-400" />
                  <span className="text-sm font-semibold text-white flex-1 text-left">
                    {t("Ver Chat da Corrida")}
                  </span>
                  <span className="text-xs text-gray-400 bg-white/10 px-2 py-0.5 rounded-full">
                    {ride.chatMessages.length} {ride.chatMessages.length === 1 ? 'mensagem' : 'mensagens'}
                  </span>
                </button>
              </Card>
            )}

            {/* Refund Section */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <MdOutlineMoneyOff className="h-5 w-5 text-rose-400" />
                <span className="text-lg font-semibold text-white">{t("Reembolso")}</span>
              </div>
              {ride?.refundedAt ? (
                <div className="rounded-xl bg-rose-500/10 border border-rose-500/20 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-rose-500/20 text-rose-400 border border-rose-500/30">
                      {Number(ride.refundAmount) < Number(ride.fare) ? t("Reembolso Parcial") : t("Reembolso Integral")}
                    </span>
                    <span className="text-sm font-bold text-rose-400">{formatCurrency(ride.refundAmount)}</span>
                  </div>
                  <div className="text-xs text-gray-400 space-y-1">
                    <div>📅 {ride.refundedAt ? Formatters.formatDateTime(new Date(ride.refundedAt as string)) : '—'}</div>
                    <div>👤 {ride.refundedBy === 'admin' ? t('Admin') : t('Motorista')}</div>
                    {ride.refundReason && <div>💬 {ride.refundReason}</div>}
                  </div>
                  {/* Allow additional partial refund if not fully refunded */}
                  {Number(ride.refundAmount) < Number(ride.fare) && ride.paymentMode === 'online' && (
                    <button
                      onClick={() => {
                        const maxRefundable = Number(ride.fare || 0) - Number(ride.refundAmount || 0);
                        setRefundAmount(String(maxRefundable));
                        setRefundReason("");
                        setRefundModalOpen(true);
                      }}
                      className="mt-3 flex items-center gap-2 w-full rounded-xl bg-rose-500/10 border border-rose-500/20 px-4 py-2.5 hover:bg-rose-500/20 transition-colors"
                    >
                      <MdOutlineMoneyOff className="h-4 w-4 text-rose-400" />
                      <span className="text-xs font-semibold text-rose-300 flex-1 text-left">{t("Reembolsar Restante")}</span>
                      <span className="text-xs text-gray-400">{formatCurrency(Number(ride.fare || 0) - Number(ride.refundAmount || 0))}</span>
                    </button>
                  )}
                </div>
              ) : ride?.fare && Number(ride.fare) > 0 && ride?.paymentMode === 'online' ? (
                <button
                  onClick={() => {
                    setRefundAmount(String(ride.fare || 0));
                    setRefundReason("");
                    setRefundModalOpen(true);
                  }}
                  className="flex items-center gap-3 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 hover:bg-rose-500/10 hover:border-rose-500/20 transition-colors"
                >
                  <MdOutlineMoneyOff className="h-5 w-5 text-rose-400" />
                  <span className="text-sm font-semibold text-white flex-1 text-left">{t("Reembolsar Pagamento")}</span>
                  <span className="text-xs text-gray-400">{formatCurrency(ride.fare)}</span>
                </button>
              ) : (
                <div className="text-sm text-gray-500 bg-white/5 rounded-xl p-4 text-center">
                  {t("Corrida sem valor de tarifa")}
                </div>
              )}
            </Card>

            {/* Refund Modal */}
            {refundModalOpen && ride && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-md mx-4 border border-white/10 shadow-2xl">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <MdOutlineMoneyOff className="h-5 w-5 text-rose-400" />
                      <span className="text-lg font-bold text-white">{t("Reembolsar Corrida")} #{params.id}</span>
                    </div>
                    <button
                      onClick={() => setRefundModalOpen(false)}
                      className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="px-5 py-4 space-y-4">
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
                      <div className="text-xs text-gray-400 mb-1">{t("Tarifa da Corrida")}</div>
                      <div className="text-xl font-bold text-white">{formatCurrency(ride.fare)}</div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1.5">{t("Valor do Reembolso")}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={String(Number(ride.fare || 0) - Number(ride.refundAmount || 0))}
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white text-sm focus:border-rose-400/50 focus:outline-none transition-colors"
                        placeholder={`Max: ${ride.fare}`}
                      />
                      {Number(refundAmount) < (Number(ride.fare) - Number(ride.refundAmount || 0)) && Number(refundAmount) > 0 && (
                        <div className="mt-1 text-xs text-amber-400">
                          ⚠️ {t("Reembolso parcial")} — {formatCurrency(Number(refundAmount))} {t("de")} {formatCurrency(Number(ride.fare) - Number(ride.refundAmount || 0))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1.5">{t("Motivo")} ({t("opcional")})</label>
                      <textarea
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        rows={2}
                        className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white text-sm focus:border-rose-400/50 focus:outline-none transition-colors resize-none"
                        placeholder={t("Descreva o motivo do reembolso...")}
                      />
                    </div>
                  </div>

                  <div className="px-5 py-3 border-t border-white/10 flex justify-end gap-2">
                    <button
                      onClick={() => setRefundModalOpen(false)}
                      disabled={refundLoading}
                      className="px-4 py-2 rounded-xl bg-white/10 text-sm text-gray-300 hover:bg-white/20 transition-colors"
                    >
                      {t("Cancelar")}
                    </button>
                    <button
                      disabled={refundLoading || !refundAmount || Number(refundAmount) <= 0 || Number(refundAmount) > (Number(ride.fare) - Number(ride.refundAmount || 0))}
                      onClick={async () => {
                        setRefundLoading(true);
                        try {
                          await refundRidePaymentApi({
                            rideId: Number(params.id),
                            amount: Number(refundAmount),
                            reason: refundReason || undefined,
                          });
                          setRefundModalOpen(false);
                          getData(); // Refresh ride data
                        } catch (err: any) {
                          alert(err?.response?.data?.message || t("Erro ao processar reembolso"));
                        } finally {
                          setRefundLoading(false);
                        }
                      }}
                      className="px-5 py-2 rounded-xl bg-rose-500 text-sm font-semibold text-white hover:bg-rose-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {refundLoading ? t("Processando...") : t("Confirmar Reembolso")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Chat Modal */}
            {chatModalOpen && ride?.chatMessages && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col border border-white/10 shadow-2xl">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <MdOutlineChat className="h-5 w-5 text-green-400" />
                      <span className="text-lg font-bold text-white">{t("Chat da Corrida")} #{params.id}</span>
                    </div>
                    <button
                      onClick={() => setChatModalOpen(false)}
                      className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                    {ride.chatMessages.map((msg: any, idx: number) => {
                      const isSystem = msg.senderId === 'system' || msg.senderName === 'Sistema';
                      const isDriver = msg.senderType === 'driver';
                      // If senderType is missing, consider it unknown or default to one side consistently
                      const msgTime = msg.createdAt ? Formatters.formatTime(new Date(msg.createdAt)) : '';

                      if (isSystem) {
                        return (
                          <div key={idx} className="flex justify-center">
                            <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs px-3 py-1.5 rounded-full max-w-[85%] text-center">
                              {msg.text || msg.message}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={idx} className={`flex ${isDriver ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${isDriver
                            ? 'bg-blue-500/15 border border-blue-500/20'
                            : 'bg-purple-500/15 border border-purple-500/20'
                            }`}>
                            <div className={`text-xs font-semibold mb-1 ${isDriver ? 'text-blue-400' : 'text-purple-400'}`}>
                              {msg.senderName || (isDriver ? t('Motorista') : t('Passageiro'))}
                            </div>
                            <div className="text-sm text-white/90">{msg.text || msg.message}</div>
                            {msgTime && <div className="text-right text-[10px] text-gray-500 mt-1">{msgTime}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Modal Footer */}
                  <div className="px-5 py-3 border-t border-white/10 flex justify-end">
                    <button
                      onClick={() => setChatModalOpen(false)}
                      className="px-4 py-2 rounded-xl bg-white/10 text-sm text-gray-300 hover:bg-white/20 transition-colors"
                    >
                      {t("Fechar")}
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/* Driver/Rider/Vehicle */}
            <Card className="p-5">
              {ride?.driver && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <MdOutlineDirectionsCar className="h-5 w-5 text-blue-400" />
                    <span className="text-lg font-semibold text-white">{t("Motorista")}</span>
                  </div>
                  <div className="flex items-center gap-4 mb-4">
                    {driverImagePath ? (
                      <ImageWithFallback src={driverImagePath} alt={t("Driver")} fallbackSrc={dummyProfile} />
                    ) : (
                      <img src={dummyProfile} alt={t("Driver")} className="h-14 w-14 rounded-xl" />
                    )}
                    <div>
                      <div className="text-base font-semibold text-white">
                        {ride.driver.firstName} {ride.driver.lastName}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <FaPhoneAlt className="h-3 w-3" />
                        {ride.driver.mobileNumber ? Formatters.formatPhoneNumber(ride.driver.mobileNumber) : "—"}
                      </div>
                    </div>
                  </div>

                  {/* Vehicle */}
                  <div className="flex items-center gap-4 rounded-xl bg-white/5 border border-white/10 p-3 mb-4">
                    {vehicleImagePath ? (
                      <ImageWithFallback src={vehicleImagePath} alt={t("Vehicle")} fallbackSrc={dummyCar} />
                    ) : (
                      <img src={dummyCar} alt={t("Vehicle")} className="h-14 w-14 rounded-xl" />
                    )}
                    <div>
                      <div className="text-sm font-semibold text-white">
                        {ride.driver.vehicle?.vehicleModel || "—"}
                      </div>
                      <div className="text-xs text-gray-400 font-mono">
                        {ride.driver.vehicle?.vehicleNumber || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-white/10 my-4" />
                </>
              )}

              <div className="flex items-center gap-2 mb-3">
                <MdOutlinePerson className="h-5 w-5 text-purple-400" />
                <span className="text-lg font-semibold text-white">{t("Passageiro")}</span>
              </div>
              <div className="flex items-center gap-4">
                {riderImagePath ? (
                  <ImageWithFallback src={riderImagePath} alt={t("Rider")} fallbackSrc={dummyProfile} />
                ) : (
                  <img src={dummyProfile} alt={t("Rider")} className="h-14 w-14 rounded-xl" />
                )}
                <div>
                  <div className="text-base font-semibold text-white">
                    {ride?.rider?.firstName || "—"}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <FaPhoneAlt className="h-3 w-3" />
                    {ride?.rider?.mobileNumber ? Formatters.formatPhoneNumber(ride.rider.mobileNumber) : "—"}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column: Map */}
          <div className="col-span-12 lg:col-span-7">
            <Card className="p-0 overflow-hidden sticky top-4">
              <MapContainer
                center={mapCenter}
                zoom={13}
                style={{
                  width: "100%",
                  height: "calc(100vh - 120px)",
                  borderRadius: "16px",
                }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />

                {ride?.pickUpLocation?.[0] && ride?.pickUpLocation?.[1] && (
                  <Marker
                    position={[ride.pickUpLocation[0], ride.pickUpLocation[1]]}
                    icon={createLabelIcon('P', '#22c55e')}
                  >
                    <Popup>{t('Pickup')}: {ride?.pickUpAddress}</Popup>
                  </Marker>
                )}

                {ride?.dropLocation?.[0] && ride?.dropLocation?.[1] && (
                  <Marker
                    position={[ride.dropLocation[0], ride.dropLocation[1]]}
                    icon={createLabelIcon('D', '#ef4444')}
                  >
                    <Popup>{t('Destination')}: {ride?.dropAddress}</Popup>
                  </Marker>
                )}

                {driverLocation?.[0] && driverLocation?.[1] && (
                  <Marker
                    position={[driverLocation[0], driverLocation[1]]}
                    icon={createLabelIcon('DR', '#3b82f6')}
                  >
                    <Popup>{t('Driver Location')}</Popup>
                  </Marker>
                )}

                {ride?.pickupToDropPath && ride.pickupToDropPath.length > 0 && (
                  <Polyline
                    positions={convertPathToLatLng(ride.pickupToDropPath)}
                    pathOptions={{ color: "#00C2FF", weight: 4, opacity: 0.7 }}
                  />
                )}

                {ride?.realPath && ride.realPath.length > 0 && (
                  <Polyline
                    positions={convertPathToLatLng(ride.realPath)}
                    pathOptions={{ color: "#8B5CF6", weight: 4, opacity: 0.7 }}
                  />
                )}
              </MapContainer>
            </Card>
          </div>
        </div>
      )}
    </>
  );
};

export default RideView;
